// LocalVault — IndexedDB-backed encrypted vault.
//
// IndexedDB stores only ciphertext. After unlock(), plaintext entries live in
// memory inside this instance. lock() drops all in-memory references.

import { openDB, deleteDB } from 'idb';
import {
  DEFAULT_ITERATIONS,
  DecryptionError,
  decryptBytes,
  decryptVault,
  deriveKey,
  encryptBytes,
  encryptVault,
  generateSalt,
  wipeKey,
} from '../crypto/vault-crypto.js';
import { packBlob, unpackBlob } from './blob-codec.js';

const DB_NAME = 'vault';
const DB_VERSION = 2;
const META = 'meta';
const VAULT = 'vault';
const ATTACHMENTS = 'attachments';
const VAULT_KEY = 'current';
const VAULT_SCHEMA_VERSION = 1;

export class VaultLockedError extends Error {
  constructor(msg = 'vault is locked') {
    super(msg);
    this.name = 'VaultLockedError';
  }
}

export class VaultExistsError extends Error {
  constructor(msg = 'a vault already exists') {
    super(msg);
    this.name = 'VaultExistsError';
  }
}

function nowMs() { return Date.now(); }

function newId() {
  // Random 128-bit hex id. Unique enough for a personal vault; no entry-count
  // assumption baked in.
  const buf = new Uint8Array(16);
  globalThis.crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

function emptyVaultData() {
  const t = nowMs();
  return {
    vaultVersion: VAULT_SCHEMA_VERSION,
    createdAt: t,
    modifiedAt: t,
    entries: [],
    folders: [],
  };
}

export class LocalVault {
  /**
   * @param {object} [opts]
   * @param {string} [opts.dbName] override DB name (useful for tests)
   */
  constructor(opts = {}) {
    this._dbName = opts.dbName ?? DB_NAME;
    this._db = null;
    this._key = null;           // CryptoKey (in memory only)
    this._salt = null;          // Uint8Array
    this._iterations = DEFAULT_ITERATIONS;
    this._data = null;          // plaintext { vaultVersion, createdAt, modifiedAt, entries, folders }
    this._changeListeners = new Set();
  }

  /** Subscribe to mutation events. Returns an unsubscribe function. */
  onChange(fn) {
    this._changeListeners.add(fn);
    return () => this._changeListeners.delete(fn);
  }

  /** The highest modifiedAt across the in-memory vault. Used by sync. */
  vaultModifiedAt() {
    return this._data?.modifiedAt ?? 0;
  }

  /** Number of non-tombstone entries. Used by sync to decide first-sync flow. */
  entryCount() {
    if (!this._data) return 0;
    return this._data.entries.filter((e) => !e.deleted).length;
  }

  async getLastSynced() {
    await this.init();
    const [at, hash] = await Promise.all([
      this._db.get(META, 'lastSyncedAt'),
      this._db.get(META, 'lastSyncedHash'),
    ]);
    return { at: at ?? 0, hash: hash ?? null };
  }

  async setLastSynced({ at, hash }) {
    await this.init();
    const tx = this._db.transaction([META], 'readwrite');
    await Promise.all([
      tx.objectStore(META).put(at, 'lastSyncedAt'),
      tx.objectStore(META).put(hash, 'lastSyncedHash'),
      tx.done,
    ]);
  }

  /** Decrypt a remote blob (bytes from PuterSync.pull) and replace local. */
  async replaceFromBlob(bytes) {
    this._requireUnlocked();
    const incoming = unpackBlob(bytes);
    // Sync targets a single account, so the remote blob must carry the same KDF
    // parameters we already hold. Enforce that BEFORE decrypting: the PWVT
    // header is outside the AES-GCM tag, so salt/iterations are unauthenticated
    // and a tampered remote could otherwise hand us a forged salt. Decryption
    // would still succeed (it uses our in-memory key, not the header), and we
    // would persist the forged salt — after which deriveKey(password, salt)
    // never reproduces the key and the local vault is permanently unopenable.
    if (incoming.iterations !== this._iterations || !bytesEqual(incoming.salt, this._salt)) {
      throw new Error('remote vault has different key parameters — refusing to adopt it');
    }
    const data = await decryptVault(incoming, this._key);
    this._data = data;
    // Re-encrypt under a fresh nonce for the local copy. Salt/iterations are
    // unchanged by construction (checked above).
    const reBlob = await encryptVault(data, this._key);
    await this._writeMetaAndBlob({ salt: this._salt, iterations: this._iterations, blob: reBlob });
    this._emitChange({ source: 'replace' });
  }

  _emitChange(info) {
    for (const fn of this._changeListeners) {
      try { fn(info); } catch (err) { console.warn('vault.onChange listener threw', err); }
    }
  }

  async init() {
    if (this._db) return;
    this._db = await openDB(this._dbName, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
        if (!db.objectStoreNames.contains(VAULT)) db.createObjectStore(VAULT);
        if (!db.objectStoreNames.contains(ATTACHMENTS)) db.createObjectStore(ATTACHMENTS);
      },
    });
  }

  async close() {
    if (this._db) {
      this._db.close();
      this._db = null;
    }
  }

  async hasVault() {
    await this.init();
    const blob = await this._db.get(VAULT, VAULT_KEY);
    return !!blob;
  }

  isUnlocked() {
    return this._key !== null && this._data !== null;
  }

  _requireUnlocked() {
    if (!this.isUnlocked()) throw new VaultLockedError();
  }

  async createVault(masterPassword) {
    await this.init();
    if (await this.hasVault()) throw new VaultExistsError();

    const salt = generateSalt();
    const iterations = DEFAULT_ITERATIONS;
    const key = await deriveKey(masterPassword, salt, iterations);
    const data = emptyVaultData();
    const blob = await encryptVault(data, key);

    await this._writeMetaAndBlob({ salt, iterations, blob });

    this._key = key;
    this._salt = salt;
    this._iterations = iterations;
    this._data = data;
  }

  async unlock(masterPassword) {
    await this.init();
    const meta = await this._readMeta();
    const blob = await this._db.get(VAULT, VAULT_KEY);
    if (!meta || !blob) throw new Error('no vault to unlock — call createVault first');

    const normalized = {
      version: blob.version,
      nonce: asU8(blob.nonce),
      ciphertext: asU8(blob.ciphertext),
    };
    const key = await deriveKey(masterPassword, meta.salt, meta.iterations);
    const data = await decryptVault(normalized, key);

    this._key = key;
    this._salt = meta.salt;
    this._iterations = meta.iterations;
    this._data = data;
  }

  lock() {
    if (this._key) wipeKey(this._key);
    this._key = null;
    this._data = null;
    this._salt = null;
  }

  // --- entries ---

  async getAll() {
    this._requireUnlocked();
    return this._data.entries.filter((e) => !e.deleted).map((e) => ({ ...e }));
  }

  /** Includes tombstones — used by sync. */
  async getAllRaw() {
    this._requireUnlocked();
    return this._data.entries.map((e) => ({ ...e }));
  }

  async get(id) {
    this._requireUnlocked();
    const found = this._data.entries.find((e) => e.id === id && !e.deleted);
    return found ? { ...found } : null;
  }

  async upsert(entry) {
    this._requireUnlocked();
    if (!entry || typeof entry !== 'object') throw new Error('entry must be an object');
    if (!entry.type) throw new Error('entry.type is required');

    const now = nowMs();
    const id = entry.id ?? newId();
    const existing = this._data.entries.find((e) => e.id === id);
    const next = {
      ...entry,
      id,
      createdAt: existing?.createdAt ?? entry.createdAt ?? now,
      modifiedAt: now,
      deleted: false,
    };
    delete next.deletedAt;

    if (existing) {
      this._data.entries = this._data.entries.map((e) => (e.id === id ? next : e));
    } else {
      this._data.entries.push(next);
    }
    await this._persist();
    return { ...next };
  }

  async remove(id) {
    this._requireUnlocked();
    const idx = this._data.entries.findIndex((e) => e.id === id);
    if (idx === -1) return false;
    const now = nowMs();
    // Soft delete: keep id + type + tombstone fields, drop secrets.
    const old = this._data.entries[idx];
    const attachmentIds = (old.attachments ?? []).map((a) => a?.id).filter(Boolean);
    this._data.entries[idx] = {
      id: old.id,
      type: old.type,
      createdAt: old.createdAt,
      modifiedAt: now,
      deleted: true,
      deletedAt: now,
    };
    await this._persist();
    // Drop the attachment ciphertext too. The tombstone no longer references
    // these rows, so without this the bytes of a "deleted" attachment stay in
    // IndexedDB forever on any device that does not pull (offline-only use, or
    // a device that only ever pushes) — delete would not delete. Runs after
    // _persist so a failure here leaves harmless orphans, not a dangling ref.
    for (const attId of attachmentIds) {
      try { await this._db.delete(ATTACHMENTS, attId); }
      catch (err) { console.warn('failed to drop attachment bytes for deleted entry', err); }
    }
    return true;
  }

  // --- export / import ---

  async export() {
    this._requireUnlocked();
    // Re-encrypt with a fresh nonce so the exported file isn't byte-identical
    // to whatever's currently in IDB.
    const blob = await encryptVault(this._data, this._key);
    blob.salt = this._salt;
    blob.iterations = this._iterations;
    return packBlob(blob);
  }

  /**
   * Replace the current vault with one decrypted from `bytes` using
   * `masterPassword`. The vault becomes unlocked under the imported password.
   */
  async import(bytes, masterPassword) {
    await this.init();
    const incoming = unpackBlob(bytes);
    const key = await deriveKey(masterPassword, incoming.salt, incoming.iterations);
    let data;
    try {
      data = await decryptVault(incoming, key);
    } catch (err) {
      throw new DecryptionError('import failed (wrong password or corrupt file)', { cause: err });
    }

    // Re-encrypt under a fresh nonce and persist with the imported salt/iters.
    const reBlob = await encryptVault(data, key);
    await this._writeMetaAndBlob({
      salt: incoming.salt,
      iterations: incoming.iterations,
      blob: reBlob,
    });

    if (this._key) wipeKey(this._key);
    this._key = key;
    this._salt = incoming.salt;
    this._iterations = incoming.iterations;
    this._data = data;
  }

  /**
   * Wipe everything by deleting the whole IndexedDB database. Caller should
   * be very sure. Closes the current connection first; if another tab still
   * has the DB open the delete is blocked — we surface that.
   */
  async destroy() {
    this.lock();
    if (this._db) {
      this._db.close();
      this._db = null;
    }
    await deleteDB(this._dbName, {
      blocked() {
        throw new Error('Another tab has the vault open. Close other tabs and try again.');
      },
    });
  }

  // --- attachments ---

  /**
   * Persist a binary attachment under `entryId` and add a metadata record to
   * the entry. Returns the assigned attachment id.
   */
  async putAttachment(entryId, { name, type, bytes }) {
    this._requireUnlocked();
    const entry = this._data.entries.find((e) => e.id === entryId && !e.deleted);
    if (!entry) throw new Error(`putAttachment: no entry ${entryId}`);
    if (!(bytes instanceof Uint8Array)) throw new Error('putAttachment: bytes must be Uint8Array');

    const id = newId();
    const blob = await encryptBytes(bytes, this._key);
    await this._db.put(ATTACHMENTS, { entryId, nonce: blob.nonce, ciphertext: blob.ciphertext }, id);

    entry.attachments = Array.isArray(entry.attachments) ? entry.attachments.slice() : [];
    entry.attachments.push({ id, name: name ?? 'file', type: type ?? '', size: bytes.byteLength });
    entry.modifiedAt = nowMs();
    await this._persist();
    return id;
  }

  /** Decrypt and return the raw bytes for an attachment. */
  async getAttachmentBytes(attachmentId) {
    this._requireUnlocked();
    const row = await this._db.get(ATTACHMENTS, attachmentId);
    if (!row) throw new Error(`getAttachmentBytes: no attachment ${attachmentId}`);
    return decryptBytes(
      { version: 1, nonce: asU8(row.nonce), ciphertext: asU8(row.ciphertext) },
      this._key,
    );
  }

  async deleteAttachment(entryId, attachmentId) {
    this._requireUnlocked();
    const entry = this._data.entries.find((e) => e.id === entryId && !e.deleted);
    if (!entry) return false;
    entry.attachments = (entry.attachments ?? []).filter((a) => a.id !== attachmentId);
    entry.modifiedAt = nowMs();
    // Persist the manifest first, then drop the bytes. The reverse order (what
    // this used to do) leaves the entry referencing a row that no longer exists
    // if anything fails in between — an attachment the UI can never open and
    // sync can never repair. This way a failure leaves a harmless orphan row,
    // which reconcile already cleans up. Mirrors putAttachment's ordering.
    await this._persist();
    await this._db.delete(ATTACHMENTS, attachmentId);
    return true;
  }

  // --- raw attachment access (no crypto) — used only by sync ---

  /** Ids of attachment blobs physically present in the local store. */
  async listAttachmentIds() {
    return this._db.getAllKeys(ATTACHMENTS);
  }

  /** [{id, entryId}] the manifest references (non-deleted entries). */
  referencedAttachments() {
    this._requireUnlocked();
    const out = [];
    for (const e of this._data.entries) {
      if (e.deleted || !Array.isArray(e.attachments)) continue;
      for (const a of e.attachments) out.push({ id: a.id, entryId: e.id });
    }
    return out;
  }

  /** Raw ciphertext row for an attachment, or null. Does NOT decrypt. */
  async getRawAttachment(id) {
    const row = await this._db.get(ATTACHMENTS, id);
    if (!row) return null;
    return { nonce: asU8(row.nonce), ciphertext: asU8(row.ciphertext) };
  }

  /** Store a downloaded ciphertext blob as-is (no encryption, no metadata change). */
  async putRawAttachment(id, entryId, { nonce, ciphertext }) {
    await this._db.put(ATTACHMENTS, { entryId, nonce, ciphertext }, id);
  }

  /** Delete only the stored blob row (no metadata change) — local orphan cleanup. */
  async deleteRawAttachment(id) {
    await this._db.delete(ATTACHMENTS, id);
  }

  // --- internal ---

  async _readMeta() {
    const [salt, iterations] = await Promise.all([
      this._db.get(META, 'salt'),
      this._db.get(META, 'iterations'),
    ]);
    if (!salt || !iterations) return null;
    return { salt: asU8(salt), iterations };
  }

  async _writeMetaAndBlob({ salt, iterations, blob }) {
    const tx = this._db.transaction([META, VAULT], 'readwrite');
    await Promise.all([
      tx.objectStore(META).put(salt, 'salt'),
      tx.objectStore(META).put(iterations, 'iterations'),
      tx.objectStore(META).put(nowMs(), 'lastModifiedAt'),
      tx.objectStore(VAULT).put(toStorable(blob), VAULT_KEY),
      tx.done,
    ]);
  }

  async _persist() {
    this._data.modifiedAt = nowMs();
    const blob = await encryptVault(this._data, this._key);
    const tx = this._db.transaction([META, VAULT], 'readwrite');
    await Promise.all([
      tx.objectStore(VAULT).put(toStorable(blob), VAULT_KEY),
      tx.objectStore(META).put(this._data.modifiedAt, 'lastModifiedAt'),
      tx.done,
    ]);
    this._emitChange({ source: 'mutate' });
  }
}

// Stored shape in IDB: drop the redundant salt/iterations (they live in `meta`).
function toStorable(blob) {
  return {
    version: blob.version,
    nonce: blob.nonce,
    ciphertext: blob.ciphertext,
  };
}

// Normalize whatever IDB hands back into a Uint8Array. Browsers and the
// fake-indexeddb shim may return ArrayBuffer, Buffer, or Uint8Array depending
// on the structured-clone implementation.
function asU8(x) {
  if (x instanceof Uint8Array) return x;
  if (x instanceof ArrayBuffer) return new Uint8Array(x);
  if (ArrayBuffer.isView(x)) return new Uint8Array(x.buffer, x.byteOffset, x.byteLength);
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer?.(x)) {
    return new Uint8Array(x.buffer, x.byteOffset, x.byteLength);
  }
  throw new Error('expected binary data from IDB');
}

function bytesEqual(a, b) {
  if (!a || !b) return false;
  const x = asU8(a);
  const y = asU8(b);
  if (x.byteLength !== y.byteLength) return false;
  for (let i = 0; i < x.byteLength; i++) if (x[i] !== y[i]) return false;
  return true;
}
